import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface Itinerary {
  id: string;
  title: string;
  destination: string | null;
  duration: number | null;
}

interface ItinerarySelectorProps {
  selectedItineraryIds: string[];
  onChange: (itineraryIds: string[]) => void;
}

export const ItinerarySelector = ({ selectedItineraryIds, onChange }: ItinerarySelectorProps) => {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const renderCount = useRef(0);
  const fetchAttempts = useRef(0);
  
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[DEBUG] ItinerarySelector RENDER #${renderCount.current}`);
    console.log(`[DEBUG] ItinerarySelector props - selectedIds: ${selectedItineraryIds.join(',')}`);
  });

  useEffect(() => {
    const fetchItineraries = async () => {
      fetchAttempts.current += 1;
      console.log(`[DEBUG] ItinerarySelector - fetchItineraries attempt #${fetchAttempts.current}`);
      
      setLoading(true);
      try {
        console.log('[DEBUG] ItinerarySelector - Starting Supabase query for itineraries');
        const { data, error } = await supabase
          .from('itineraries')
          .select('id, title, destination, duration')
          .eq('status', 'published')
          .order('title', { ascending: true });

        if (error) {
          console.error('[DEBUG] ItinerarySelector - Supabase error:', error);
          throw error;
        }
        
        console.log(`[DEBUG] ItinerarySelector - Successfully fetched ${data?.length || 0} itineraries`);
        setItineraries(data || []);
      } catch (error: any) {
        console.error('[DEBUG] ItinerarySelector - Error fetching itineraries:', error.message);
      } finally {
        console.log('[DEBUG] ItinerarySelector - Setting loading to false');
        setLoading(false);
      }
    };

    fetchItineraries();
  }, []);

  const toggleItinerary = (itineraryId: string) => {
    console.log(`[DEBUG] ItinerarySelector - toggling itinerary: ${itineraryId}`);
    if (selectedItineraryIds.includes(itineraryId)) {
      onChange(selectedItineraryIds.filter(id => id !== itineraryId));
    } else {
      onChange([...selectedItineraryIds, itineraryId]);
    }
  };

  const handleDialogClose = () => {
    console.log('[DEBUG] ItinerarySelector - dialog closed');
    setDialogOpen(false);
  };

  const selectedItinerariesInfo = itineraries
    .filter(itinerary => selectedItineraryIds.includes(itinerary.id))
    .map(itinerary => ({
      id: itinerary.id,
      title: itinerary.title,
      destination: itinerary.destination,
      duration: itinerary.duration
    }));

  console.log(`[DEBUG] ItinerarySelector - Selected itineraries count: ${selectedItinerariesInfo.length}`);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Related Itineraries</Label>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">Select Itineraries</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Select Related Itineraries</DialogTitle>
            </DialogHeader>
            
            {loading ? (
              <div className="flex justify-center p-4">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <div className="grid gap-2 max-h-[60vh] overflow-y-auto py-2">
                {itineraries.length === 0 ? (
                  <p className="text-center text-muted-foreground p-4">
                    No published itineraries found. Create some first!
                  </p>
                ) : (
                  itineraries.map((itinerary) => (
                    <div
                      key={itinerary.id}
                      className={`flex items-center justify-between p-3 rounded-md cursor-pointer transition-colors ${
                        selectedItineraryIds.includes(itinerary.id)
                          ? 'bg-primary/10 border border-primary/30'
                          : 'hover:bg-accent border border-transparent'
                      }`}
                      onClick={() => toggleItinerary(itinerary.id)}
                    >
                      <div className="space-y-1">
                        <h4 className="font-medium">{itinerary.title}</h4>
                        <div className="text-sm text-muted-foreground">
                          {itinerary.destination && (
                            <span className="mr-3">{itinerary.destination}</span>
                          )}
                          {itinerary.duration && (
                            <span>{itinerary.duration} day{itinerary.duration !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>
                      {selectedItineraryIds.includes(itinerary.id) && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            <div className="flex justify-end">
              <Button onClick={handleDialogClose}>Done</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {selectedItinerariesInfo.length > 0 && (
        <div className="space-y-2">
          {selectedItinerariesInfo.map((itinerary) => (
            <Card key={itinerary.id} className="border border-primary/30 bg-primary/5">
              <CardContent className="flex items-center justify-between p-3">
                <div>
                  <p className="font-medium">{itinerary.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {itinerary.destination && `${itinerary.destination} · `}
                    {itinerary.duration && `${itinerary.duration} day${itinerary.duration !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => toggleItinerary(itinerary.id)}
                >
                  Remove
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
